// ModelFormModal.jsx
//
// Create or edit a forecast model, including its channel personas.
// One modal, two modes via the `mode` prop ('create' | 'edit').
//
// In CREATE mode:
//   - User fills in model fields and channel personas
//   - On save → POST /forecast/models with channel_personas in body
//   - Server creates everything atomically in a transaction
//
// In EDIT mode:
//   - Opens, fetches the model + its personas via GET /forecast/models/:id
//   - User edits both model fields and personas
//   - On save:
//       1. PUT /forecast/models/:id     (update model fields)
//       2. For each persona row: POST (new), PUT (changed), DELETE (removed)
//   - Multiple sequential API calls; partial failure is surfaced
//
// Each channel carries a required `direction` (Outbound | Inbound) so the
// Channel Mix grid can subtotal the two sides separately.

import React, { useEffect, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, MenuItem,
  Select, Switch, TextField, Typography,
} from "@mui/material";

import {
  getForecastModel, createForecastModel, updateForecastModel,
  createChannelPersona, updateChannelPersona, deleteChannelPersona,
} from "../../../services/forecast/forecastservices";

const BLUE  = "#378ADD";
const RED   = "#A32D2D";

const DIRECTIONS = ["Outbound", "Inbound"];


// Default personas pre-filled for the create flow.
const DEFAULT_PERSONAS = [
  // Channels carry name/direction/description/order. The per-day share lives
  // in the "Channel Mix by Day" grid (saved to model_channel_day_shares).
  // share_of_contacts on channel_personas is kept at 0 here purely for
  // backend compatibility - the column still exists but isn't surfaced.
  // direction starts blank so the (required) dropdown forces a choice, the
  // same way the (required) name field starts blank.
  { name: "", direction: "", share_of_contacts: 0, display_order: 1 },
  { name: "", direction: "", share_of_contacts: 0, display_order: 2 },
  { name: "", direction: "", share_of_contacts: 0, display_order: 3 },
];

// Shared grid template for the channel header + rows.
// Name | Direction | Description | Order | delete
const PERSONA_GRID = "1.3fr 1fr 2fr 0.7fr 40px";


export default function ModelFormModal({ open, mode, modelId, onClose, onSaved }) {
  const isEdit = mode === "edit";

  const [form, setForm] = useState({
    name: "",
    description: "",
    forecast_horizon_months: 9,
    min_history_threshold: 3,
    is_active: true,
  });
  const [personas,         setPersonas]         = useState([]);
  const [originalPersonas, setOriginalPersonas] = useState([]);
  const [deletedIds,       setDeletedIds]       = useState([]);


  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");

  // ── Reset / load when modal opens ─────────────────────────────
  useEffect(() => {
    if (!open) return;
    setError("");
    setDeletedIds([]);

    if (isEdit && modelId) {
      // Fetch model + personas
      setLoading(true);
      getForecastModel(modelId)
        .then(data => {
          setForm({
            name:                     data.name || "",
            description:              data.description || "",
            forecast_horizon_months:  data.forecast_horizon_months ?? 9,
            min_history_threshold:    data.min_history_threshold ?? 3,
            is_active:                data.is_active === false ? false : true,
          });
          const cp = (data.channel_personas || []).map(p => ({
            id:                p.id,
            name:              p.name,
            description:       p.description || "",
            direction:         p.direction === "Inbound" ? "Inbound" : "Outbound",
            share_of_contacts: Number(p.share_of_contacts),
            display_order:     p.display_order,
            is_active:         p.is_active !== false,
          }));
          setPersonas(cp);
          setOriginalPersonas(JSON.parse(JSON.stringify(cp)));

        })
        .catch(e => {
          setError(`Failed to load model: ${e?.response?.data?.error || e?.message}`);
        })
        .finally(() => setLoading(false));
    } else {
      // Create mode - reset to defaults
      setForm({
        name: "",
        description: "",
        forecast_horizon_months: 9,
        min_history_threshold: 3,
        is_active: true,
      });
      setPersonas(DEFAULT_PERSONAS.map(p => ({ ...p, description: "", is_active: true })));
      setOriginalPersonas([]);
    }
  }, [open, isEdit, modelId]);

  // ── Field setters ─────────────────────────────────────────────
  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const setPersonaField = (idx, key, value) =>
    setPersonas(prev => prev.map((p, i) => i === idx ? { ...p, [key]: value } : p));

  const addPersona = () => {
    setPersonas(prev => [
      ...prev,
      { name: "", description: "", direction: "", share_of_contacts: 0,
        display_order: (prev.length + 1) * 10, is_active: true },
    ]);
  };

  const removePersona = (idx) => {
    setPersonas(prev => {
      const removed = prev[idx];
      if (removed?.id != null) {
        setDeletedIds(d => [...d, removed.id]);
        // Note: this channel's per-day shares get cleaned up server-side
        // when the channel is deleted (FK cascade) - no client work needed.
      }
      return prev.filter((_, i) => i !== idx);
    });
  };



  // ── Validation ────────────────────────────────────────────────
  const validate = () => {
    if (!form.name.trim())               return "Name is required";
    if (form.name.trim().length > 200)   return "Name is too long (max 200 chars)";
    if (form.forecast_horizon_months < 1 || form.forecast_horizon_months > 36)
      return "Forecast horizon must be 1-36 months";
    if (form.min_history_threshold < 1 || form.min_history_threshold > 24)
      return "Min history threshold must be 1-24 months";

    // Channel name + direction validation (share validation moved to the
    // per-day grid - we allow any value there since channels can overlap
    // or under-cover).
    for (const [i, p] of personas.entries()) {
      if (!p.name?.trim())                  return `Channel #${i + 1}: name is required`;
      if (!DIRECTIONS.includes(p.direction)) return `Channel #${i + 1}: direction (Outbound/Inbound) is required`;
    }
    return null;
  };

  // ── Save ──────────────────────────────────────────────────────
  const handleSave = async () => {
    const v = validate();
    if (v) { setError(v); return; }

    setSaving(true); setError("");
    try {
      if (isEdit) {
        // Update model fields
        await updateForecastModel(modelId, {
          name:                     form.name.trim(),
          description:              form.description.trim() || null,
          forecast_horizon_months:  Number(form.forecast_horizon_months),
          min_history_threshold:    Number(form.min_history_threshold),
          is_active:                form.is_active,
        });

        // Diff personas
        const origById = new Map(originalPersonas.map(p => [p.id, p]));

        for (const id of deletedIds) {
          await deleteChannelPersona(id);
        }

        for (const p of personas) {
          const payload = {
            name:              p.name.trim(),
            description:       p.description?.trim() || null,
            direction:         p.direction,
            share_of_contacts: Number(p.share_of_contacts),
            display_order:     p.display_order ?? 100,
            is_active:         p.is_active !== false,
          };
          if (p.id == null) {
            // New
            await createChannelPersona(modelId, payload);
          } else {
            // Updated?
            const orig = origById.get(p.id);
            const changed = !orig
              || orig.name !== payload.name
              || (orig.description || null) !== payload.description
              || orig.direction !== payload.direction
              || Number(orig.share_of_contacts) !== payload.share_of_contacts
              || orig.display_order !== payload.display_order
              || orig.is_active !== payload.is_active;
            if (changed) {
              await updateChannelPersona(p.id, payload);
            }
          }
        }
      } else {
        // Create mode - one atomic POST
        await createForecastModel({
          name:                     form.name.trim(),
          description:              form.description.trim() || null,
          forecast_horizon_months:  Number(form.forecast_horizon_months),
          min_history_threshold:    Number(form.min_history_threshold),
          channel_personas: personas.map(p => ({
            name:              p.name.trim(),
            description:       p.description?.trim() || null,
            direction:         p.direction,
            share_of_contacts: Number(p.share_of_contacts) || 0,
            display_order:     p.display_order ?? 100,
          })),
        });
        // Day shares can't be saved in create mode because new channel IDs
        // aren't known until after the model exists. User re-opens Edit
        // to set them.
      }

      onSaved?.();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 500, pb: 1 }}>
        {isEdit ? "Edit Model" : "New Model"}
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
            <CircularProgress size={20} /> Loading model…
          </Box>
        ) : (
          <>
            {/* ── Model fields ── */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
              <TextField
                label="Name" required size="small" fullWidth
                value={form.name}
                onChange={e => setField("name", e.target.value)}
                disabled={saving}
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Active</Typography>
                <Switch
                  checked={form.is_active}
                  onChange={e => setField("is_active", e.target.checked)}
                  disabled={saving}
                />
              </Box>
            </Box>

            <TextField
              label="Description" size="small" fullWidth multiline rows={2}
              value={form.description}
              onChange={e => setField("description", e.target.value)}
              disabled={saving}
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
              <TextField
                label="Forecast horizon (months)" type="number" size="small" fullWidth
                inputProps={{ min: 1, max: 36 }}
                value={form.forecast_horizon_months}
                onChange={e => setField("forecast_horizon_months", Number(e.target.value))}
                disabled={saving}
              />
              <TextField
                label="Min history threshold (months)" type="number" size="small" fullWidth
                inputProps={{ min: 1, max: 24 }}
                value={form.min_history_threshold}
                onChange={e => setField("min_history_threshold", Number(e.target.value))}
                disabled={saving}
              />
            </Box>

            {/* ── Channels ── */}
            <Box sx={{
              display: "flex", justifyContent: "space-between", alignItems: "baseline",
              mt: 3, mb: 1,
            }}>
              <Typography sx={{
                fontSize: 11, fontWeight: 500, color: "text.secondary",
                textTransform: "uppercase", letterSpacing: "0.05em",
              }}>
                Channels
              </Typography>
              <Typography sx={{ fontSize: 10, color: "text.disabled" }}>
                Daily share configured below
              </Typography>
            </Box>

            {/* Header row */}
            <Box sx={{
              display: "grid",
              gridTemplateColumns: PERSONA_GRID,
              gap: 1, mb: 0.5, px: 1,
              fontSize: 10, color: "text.secondary",
              textTransform: "uppercase", letterSpacing: "0.05em",
            }}>
              <Box>Name</Box>
              <Box>Direction</Box>
              <Box>Description</Box>
              <Box>Order</Box>
              <Box />
            </Box>

            {personas.map((p, i) => {
              const dirMissing = !DIRECTIONS.includes(p.direction);
              return (
                <Box key={i} sx={{
                  display: "grid",
                  gridTemplateColumns: PERSONA_GRID,
                  gap: 1, mb: 1, alignItems: "center",
                }}>
                  <TextField
                    size="small" placeholder="e.g., Outbound Texts"
                    value={p.name}
                    onChange={e => setPersonaField(i, "name", e.target.value)}
                    disabled={saving}
                    inputProps={{ style: { fontSize: 12 } }}
                  />
                  <Select
                    size="small"
                    displayEmpty
                    value={p.direction || ""}
                    onChange={e => setPersonaField(i, "direction", e.target.value)}
                    disabled={saving}
                    error={dirMissing}
                    renderValue={(val) =>
                      val
                        ? <span style={{ fontSize: 12 }}>{val}</span>
                        : <span style={{ fontSize: 12, color: "#bbb" }}>Select…</span>}
                    sx={{
                      fontSize: 12,
                      ".MuiSelect-select": { fontSize: 12, py: "8.5px" },
                    }}
                  >
                    {DIRECTIONS.map(d => (
                      <MenuItem key={d} value={d} sx={{ fontSize: 12 }}>{d}</MenuItem>
                    ))}
                  </Select>
                  <TextField
                    size="small" placeholder="optional"
                    value={p.description}
                    onChange={e => setPersonaField(i, "description", e.target.value)}
                    disabled={saving}
                    inputProps={{ style: { fontSize: 12 } }}
                  />
                  <TextField
                    size="small" type="number"
                    inputProps={{ min: 0, style: { fontSize: 12 } }}
                    value={p.display_order ?? ""}
                    onChange={e => setPersonaField(i, "display_order", Number(e.target.value))}
                    disabled={saving}
                  />
                  <IconButton
                    size="small"
                    onClick={() => removePersona(i)}
                    disabled={saving}
                    sx={{ color: RED, fontSize: 18 }}
                  >
                    ×
                  </IconButton>
                </Box>
              );
            })}

            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                onClick={addPersona}
                disabled={saving}
                sx={{
                  fontSize: 12, textTransform: "none",
                  color: BLUE, borderRadius: "20px",
                  border: `0.5px solid ${BLUE}`,
                  "&:hover": { bgcolor: `${BLUE}15` },
                }}
              >
                + Add channel
              </Button>
            </Box>

            {/* Per-day channel shares are configured via the standalone
                "Channel Mix" action button on the Models list page. */}

                        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || loading}
          variant="contained"
          sx={{
            textTransform: "none", borderRadius: "20px",
            background: BLUE, "&:hover": { background: "#2A6FB0" },
          }}
        >
          {saving ? "Saving…" : (isEdit ? "Save changes" : "Create model")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}